import { useMemo, useState } from 'react';
import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  Icon,
  IconButton,
  Select,
  Skeleton,
  Stack,
  Tag,
  Text,
  Tooltip,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  FiCalendar,
  FiCheck,
  FiCheckCircle,
  FiChevronDown,
  FiChevronRight,
  FiCreditCard,
  FiFileText,
  FiFolder,
  FiHeart,
  FiHome,
  FiList,
  FiLoader,
  FiRefreshCw,
  FiRotateCcw,
  FiShield,
  FiTruck,
  FiZap,
} from 'react-icons/fi';
import { FaPaw } from 'react-icons/fa';

import SectionPanel from '../../components/SectionPanel';
import PolicyDetails from './PolicyDetails';
import { formatPolicyPremium } from './policyInformation';
import { useClientCenterCustomerPolicies } from '../../hooks';
import {
  formatPolicyDate,
  groupPolicies,
  policyStatuses,
  policyType,
} from './policyGroups';
import type { ActurisContactPolicy } from 'api/acturis/client-center';

const presentation = (policy: ActurisContactPolicy) => {
  const name =
    `${policy.product_target_detail || ''} ${policy.product_name}`.toLowerCase();
  const commercial = /commercial|\bcl\b/.test(name);
  if (/auto|automobile|motor/.test(name))
    return {
      label:
        `${commercial ? 'CL' : /personal|\bpl\b/.test(name) ? 'PL' : ''} AUTO`.trim(),
      icon: FiTruck,
      color: '#24658a',
    };
  if (/property|habitational|home/.test(name))
    return {
      label: commercial ? 'CL PROP' : 'PL HOME',
      icon: FiHome,
      color: '#f15b25',
    };
  if (/life/.test(name))
    return { label: 'PL LIFE', icon: FiHeart, color: '#168052' };
  if (/pet/.test(name))
    return { label: 'PL PET', icon: FaPaw, color: '#f15b25' };
  return {
    label: policyType(policy).toUpperCase(),
    icon: FiShield,
    color: '#24658a',
  };
};

const badgeStyle = (status: string) => {
  if (status === 'Active') return { color: 'green', icon: FiCheckCircle };
  if (/renewal|binder/i.test(status))
    return { color: 'blue', icon: FiRefreshCw };
  if (/progress|pending|overdue/i.test(status))
    return { color: 'yellow', icon: FiLoader };
  return { color: 'gray', icon: FiFileText };
};

const quickActions = [
  { label: 'Renewal', icon: FiRefreshCw },
  { label: 'Work in progress', icon: FiLoader },
  { label: 'Tasks', icon: FiList },
  { label: 'Billing', icon: FiCreditCard },
  { label: 'Claims', icon: FiShield },
];

const PoliciesContent = ({ customerId }: { customerId: string }) => {
  const [page, setPage] = useState(1);
  const { policies, hasNextPage, isLoading, error, refetch, isFetching } =
    useClientCenterCustomerPolicies(customerId, page);
  const [type, setType] = useState('');
  const [status, setStatus] = useState('Active');
  const background = useColorModeValue('white', 'navy.800');
  const border = useColorModeValue('gray.100', 'gray.600');
  const muted = useColorModeValue('gray.500', 'gray.400');
  const actionBg = useColorModeValue('#f8fafc', 'navy.900');
  const premiumBg = useColorModeValue('#fff0e7', '#38271f');
  const groups = useMemo(() => groupPolicies(policies), [policies]);
  const types = Array.from(
    new Set([...policies.map(policyType), ...(type ? [type] : [])]),
  ).sort();
  const activeCount = groups.filter((group) =>
    policyStatuses(group).includes('Active'),
  ).length;
  const filtered = groups.filter(
    (group) =>
      (!type ||
        group.versions.some((version) => policyType(version) === type)) &&
      (!status || policyStatuses(group).includes(status)),
  );
  const reset = () => {
    setType('');
    setStatus('Active');
  };

  return (
    <SectionPanel
      title="Policies"
      variant="plain"
      count={
        !isLoading && !error ? `${activeCount} active on this page` : undefined
      }
    >
      <Text fontSize="sm" color={muted} mb="4">
        Filters and policy versions apply to this page only. A policy may have
        versions on other pages.
      </Text>
      {isLoading ? (
        <Stack role="status" aria-label="Loading policies" spacing="4">
          <Skeleton height="132px" borderRadius="xl" />
          <Skeleton height="132px" borderRadius="xl" />
        </Stack>
      ) : error ? (
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          <Text flex="1">Unable to load policies.</Text>
          <Button size="sm" onClick={() => refetch()} isLoading={isFetching}>
            Retry
          </Button>
        </Alert>
      ) : groups.length === 0 ? (
        <Box
          bg={background}
          p="6"
          borderRadius="xl"
          borderWidth="1px"
          borderColor={border}
        >
          <Text color={muted}>
            {page === 1
              ? 'No policies found for this customer.'
              : 'No more policy records. Go to the previous page.'}
          </Text>
        </Box>
      ) : (
        <Stack spacing="4">
          <Flex gap="3" align="center" wrap="wrap">
            <Text
              as="label"
              htmlFor="policy-type-filter"
              color={muted}
              fontSize="xs"
              fontWeight="700"
              textTransform="uppercase"
            >
              Line of business
            </Text>
            <Select
              id="policy-type-filter"
              aria-label="Line of business"
              size="sm"
              width="180px"
              bg={background}
              borderColor={border}
              borderRadius="full"
              value={type}
              onChange={(event) => setType(event.target.value)}
            >
              <option value="">All lines</option>
              {types.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </Select>
            <Flex gap="2" role="group" aria-label="Policy status">
              {['Active', 'Cancelled', ''].map((value) => (
                <Button
                  key={value || 'all'}
                  size="sm"
                  borderRadius="full"
                  fontWeight="600"
                  px="4"
                  borderWidth="1px"
                  borderColor={status === value ? '#24658a' : border}
                  bg={status === value ? '#24658a' : background}
                  color={status === value ? 'white' : muted}
                  _hover={{ bg: status === value ? '#1c5273' : actionBg }}
                  aria-pressed={status === value}
                  leftIcon={status === value ? <FiCheck /> : undefined}
                  onClick={() => setStatus(value)}
                >
                  {value || 'All'}
                </Button>
              ))}
            </Flex>
            <Button
              size="sm"
              ml="auto"
              borderRadius="full"
              bg={background}
              borderWidth="1px"
              borderColor={border}
              color={muted}
              leftIcon={<FiRotateCcw />}
              onClick={reset}
            >
              Reset
            </Button>
          </Flex>
          {filtered.length === 0 ? (
            <Box
              bg={background}
              p="6"
              borderRadius="xl"
              borderWidth="1px"
              borderColor={border}
            >
              <Text color={muted}>
                No policies on this page match these filters.
              </Text>
              <Button
                mt="2"
                size="sm"
                variant="link"
                color="#24658a"
                onClick={() => {
                  setType('');
                  setStatus('');
                }}
              >
                Show all policies
              </Button>
            </Box>
          ) : (
            <Accordion key={page} allowMultiple>
              {filtered.map((group) => {
                const policy = group.versions[0];
                const visual = presentation(policy);
                const statuses = policyStatuses(group);
                return (
                  <AccordionItem
                    key={group.policyRef}
                    bg={background}
                    borderWidth="1px"
                    borderColor={border}
                    borderRadius="xl"
                    mb="4"
                    overflow="hidden"
                    boxShadow="0 2px 5px rgba(25, 45, 65, 0.06)"
                  >
                    {({ isExpanded }) => (
                      <>
                        <Flex
                          align="center"
                          gap="4"
                          p={{ base: '4', xl: '5' }}
                          wrap={{ base: 'wrap', xl: 'nowrap' }}
                        >
                          <AccordionButton
                            flex="1"
                            minW={{ base: '100%', xl: '300px' }}
                            p="0"
                            borderRadius="md"
                            textAlign="left"
                            _hover={{ bg: 'transparent' }}
                            aria-label={`${isExpanded ? 'Collapse' : 'Expand'} policy ${policy.policy_number || group.policyRef}`}
                          >
                            <Icon
                              as={isExpanded ? FiChevronDown : FiChevronRight}
                              color={muted}
                              mr="3"
                              flexShrink={0}
                            />
                            <Flex
                              align="center"
                              justify="center"
                              boxSize="54px"
                              borderRadius="14px"
                              flexShrink={0}
                              bg={visual.color}
                              color="white"
                              boxShadow="0 3px 7px rgba(30, 70, 95, .18)"
                              mr="4"
                            >
                              <Icon as={visual.icon} boxSize="25px" />
                            </Flex>
                            <Box minW="0">
                              <Flex gap="2" align="center" wrap="wrap" mb="1">
                                <Text
                                  as="h3"
                                  fontSize="md"
                                  fontWeight="800"
                                  overflowWrap="anywhere"
                                >
                                  {visual.label} ·{' '}
                                  {policy.policy_number || 'Number unavailable'}
                                </Text>
                                {statuses.map((label) => {
                                  const badge = badgeStyle(label);
                                  return (
                                    <Tag
                                      key={label}
                                      colorScheme={badge.color}
                                      size="sm"
                                      borderRadius="full"
                                      px="3"
                                      borderWidth="1px"
                                      borderColor={`${badge.color}.200`}
                                    >
                                      <Icon
                                        as={badge.icon}
                                        mr="1"
                                        boxSize="12px"
                                      />
                                      {label}
                                    </Tag>
                                  );
                                })}
                              </Flex>
                              <Text
                                fontSize="sm"
                                color={muted}
                                overflowWrap="anywhere"
                                mb="2"
                              >
                                {policy.carrier_name || 'Insurer unavailable'} ·{' '}
                                {policy.policyholder_name ||
                                  'Insured name unavailable'}
                              </Text>
                              <Tag
                                size="sm"
                                colorScheme="blue"
                                borderRadius="full"
                                fontWeight="600"
                              >
                                <Icon as={FiCalendar} mr="1.5" />
                                Term {formatPolicyDate(
                                  policy.effective_date,
                                )} – {formatPolicyDate(policy.expiry_date)}
                              </Tag>
                            </Box>
                          </AccordionButton>
                          <Flex
                            gap="3"
                            align="center"
                            wrap={{ base: 'wrap', md: 'nowrap' }}
                            width={{ base: 'full', xl: 'auto' }}
                            pl={{ base: '7', xl: '0' }}
                            flexShrink={0}
                          >
                            <Box
                              bg={actionBg}
                              borderWidth="1px"
                              borderColor={border}
                              borderRadius="xl"
                              p="3"
                            >
                              <Text
                                fontSize="10px"
                                color={muted}
                                fontWeight="700"
                                letterSpacing=".5px"
                                mb="2"
                              >
                                QUICK ACTIONS
                              </Text>
                              <Flex gap="1.5">
                                {quickActions.map((action) => (
                                  <Tooltip
                                    key={action.label}
                                    label={`${action.label} is not available yet`}
                                    hasArrow
                                  >
                                    <Box
                                      as="span"
                                      tabIndex={0}
                                      aria-label={`${action.label} is not available yet`}
                                    >
                                      <IconButton
                                        aria-label={action.label}
                                        icon={
                                          <Icon
                                            as={action.icon}
                                            boxSize="18px"
                                          />
                                        }
                                        size="sm"
                                        boxSize="32px"
                                        borderRadius="9px"
                                        bg={background}
                                        borderWidth="1px"
                                        borderColor={border}
                                        color={muted}
                                        isDisabled
                                        _disabled={{
                                          opacity: 0.55,
                                          cursor: 'not-allowed',
                                        }}
                                      />
                                    </Box>
                                  </Tooltip>
                                ))}
                              </Flex>
                            </Box>
                            <Box
                              minW="190px"
                              bg={premiumBg}
                              borderWidth="1px"
                              borderColor="#ffd5c1"
                              borderRadius="xl"
                              p="3"
                              color="#ce481c"
                            >
                              <Text
                                fontSize="10px"
                                fontWeight="700"
                                letterSpacing=".4px"
                                mb="1"
                              >
                                ESTIMATED ANNUAL PREMIUM
                              </Text>
                              <Flex
                                align="center"
                                justify="space-between"
                                gap="4"
                              >
                                <Text fontSize="xl" fontWeight="800">
                                  {formatPolicyPremium(
                                    policy.expiring_annual_premium,
                                  ) || 'Unavailable'}
                                </Text>
                                <Icon as={FiShield} boxSize="21px" />
                              </Flex>
                            </Box>
                            <Stack spacing="2" minW="116px">
                              <Tooltip
                                label="Policy actions are not available yet"
                                hasArrow
                              >
                                <Box as="span" tabIndex={0}>
                                  <Button
                                    width="full"
                                    size="sm"
                                    variant="outline"
                                    borderRadius="full"
                                    borderColor="#7ca7c4"
                                    color="#24658a"
                                    leftIcon={<FiZap />}
                                    rightIcon={<FiChevronDown />}
                                    isDisabled
                                  >
                                    Actions
                                  </Button>
                                </Box>
                              </Tooltip>
                              <Tooltip
                                label="Policy documents are not available yet"
                                hasArrow
                              >
                                <Box as="span" tabIndex={0}>
                                  <Button
                                    width="full"
                                    size="sm"
                                    variant="outline"
                                    borderRadius="full"
                                    borderColor="#7ca7c4"
                                    color="#24658a"
                                    leftIcon={<FiFolder />}
                                    isDisabled
                                  >
                                    Documents
                                  </Button>
                                </Box>
                              </Tooltip>
                            </Stack>
                          </Flex>
                        </Flex>
                        <AccordionPanel
                          px={{ base: '4', md: '6' }}
                          pt="0"
                          pb="6"
                        >
                          {isExpanded && (
                            <PolicyDetails
                              customerId={customerId}
                              policy={policy}
                              versions={group.versions}
                              lineOfBusiness={visual.label}
                            />
                          )}
                        </AccordionPanel>
                      </>
                    )}
                  </AccordionItem>
                );
              })}
            </Accordion>
          )}
        </Stack>
      )}
      <Flex
        as="nav"
        aria-label="Policy pagination"
        align="center"
        justify="flex-end"
        gap="3"
        mt="4"
      >
        <Button
          size="sm"
          onClick={() => setPage((current) => current - 1)}
          isDisabled={page === 1 || isFetching}
        >
          Previous
        </Button>
        <Text fontSize="sm" aria-live="polite">
          Page {page}
        </Text>
        <Button
          size="sm"
          onClick={() => setPage((current) => current + 1)}
          isDisabled={!hasNextPage || isFetching || Boolean(error)}
        >
          Next
        </Button>
      </Flex>
    </SectionPanel>
  );
};

const Policies = ({ customerId }: { customerId: string }) => (
  <PoliciesContent key={customerId} customerId={customerId} />
);
export default Policies;
